import { useStoreWithEqualityFn } from "zustand/traditional";

import { useAppStore } from "@/stores";
import {
  currentCampaignEqual,
  resolveCurrentCampaign,
  type CurrentCampaign,
} from "@/campaigns";

export type { CurrentCampaign };

/**
 * Resolve the selected campaign (or an explicit id) to its type + module-owned
 * payload, via the campaign-type registry. Type-agnostic: adding a campaign type
 * needs no change here — the new module's `resolve` is consulted automatically.
 */
export const useCurrentCampaign = (
  idOrKey?: string | number
): CurrentCampaign | null => {
  const selectedId = useAppStore((s) => s.selectedCampaignId);
  const key = idOrKey != null ? String(idOrKey) : (selectedId ?? "");
  return useStoreWithEqualityFn(
    useAppStore,
    (s) => resolveCurrentCampaign(s, key),
    currentCampaignEqual
  );
};

/**
 * Whether the selected campaign (or an explicit id) resolves at all — for components that only
 * gate on its existence. A boolean selector, so a progress push that replaces the campaign
 * object (missions, every second) does not re-render the gate and everything under it.
 */
export const useHasCurrentCampaign = (idOrKey?: string | number): boolean => {
  const selectedId = useAppStore((s) => s.selectedCampaignId);
  const key = idOrKey != null ? String(idOrKey) : (selectedId ?? "");
  return useAppStore((s) => resolveCurrentCampaign(s, key) !== null);
};
